import * as React from 'react';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';

import { Box, type BoxProps } from '../../components/ui/box';
import { Input } from '../../components/ui/input';
import { Popover, PopoverPopup, PopoverTrigger } from '../../components/ui/popover';
import { Text } from '../../components/ui/text';
import { cva, type VariantProps } from '../../lib/cva';
import { cn } from '../../lib/utils';

import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
} from '../entity-selector';
import type {
  EntityItemConfig,
  EntitySelection,
  EntitySelectorConfig,
} from '../entity-selector';

import { useMenuFocusStealGuard } from '../../hooks/use-menu-focus-steal-guard';
import { DataTableCellContent } from './data-table-cell';
import { useDataTableDensity } from './data-table-context';
import { useDataTableOverlay } from './data-table-overlay';
import type { DataTableTextBrightness } from './data-table-types';
import {
  CELL_ALIGNMENT_CLASS,
  DENSITY_CONFIG,
  isOwnPress,
  type DataTableCellAlignment,
} from './data-table-utils';
import type { Omittable } from "../../lib/props";

// No defaultVariants: `align` keeps its parameter default because the same
// value also drives `CELL_ALIGNMENT_CLASS` and the child `align` props.
// `weight` exists for a cell whose resting display is bolder than body text
// (a name column's medium label): the editor and the rest state both wear it,
// so entering and leaving edit mode moves no glyph.
export const dataTableEditableTextCellVariants = cva('', {
  variants: {
    align: {
      start: 'text-left',
      center: 'text-center',
      end: 'text-right',
    },
    weight: {
      medium: 'font-medium',
    },
  },
});

// The cell's own chrome, shared by every editing cell so no two of them can
// draw a different outline. The outline is a promise the cell EDITS: a
// read-only cell — a plain value, or an entity chip that only opens its
// entity and hovers to its preview — wears none, and composes bare
// `DataTableCellContent` instead.
//
// Vertical inset (my-0.5, not h-full): the hover/active ring floats as a
// rounded pill inside the row instead of pressing against the row hairlines
// and clipping its own corners. 2px is the whole inset at EVERY density — the
// ring is the cell's outline, so it tracks the row (rowHeight − 4); with more
// inset it reads as a small chip floating in the row instead.
//
// Hover is an OUTLINE, not a wash — the row already washes on hover, and a
// second wash on the cell just mushes into it; the outline is what reads as
// "this cell is its own press target". The ring is `--border-strong`: it
// always draws on a washed row, where the base border's lightness nearly
// vanishes in dark mode. The open cell paints the table's own fill
// (--table-fill, the surface as a value), which cancels the row wash beneath
// it, plus the brand border — brand, not primary: primary ships neutral in
// this system. Standalone (no table), --table-fill is undeclared →
// --background.
//
// With `outline` on, the 1px border is reserved (transparent) in every state
// so hover and active recolor without reflow. With `outline` off no border
// renders AT ALL: the outline-less shell exists for an action-launched rename
// whose REST twin is a different, borderless component (a hover-actions name
// cell), so reserving the border here is what would move the glyphs.
function cellOutlineClassName(
  align: DataTableCellAlignment,
  active: boolean,
  outline: boolean,
): string {
  return cn(
    'my-0.5',
    CELL_ALIGNMENT_CLASS[align],
    outline &&
      cn(
        'border border-transparent',
        active
          ? 'border-brand bg-[var(--table-fill,var(--background))]'
          : 'hover:border-(--border-strong)',
      ),
  );
}

// No borderColor here: Box auto-emits `border` for it, and the reserved 1px
// is outline-gated in cellOutlineClassName.
const CELL_SHELL_PROPS = {
  flex: 1,
  minWidth: 0,
  fill: 'width',
  alignSelf: 'stretch',
  direction: 'row',
  align: 'center',
  overflow: 'hidden',
  rounded: 'md',
} as const;

type DataTableCellOutlineProps = Omittable<{
  align: DataTableCellAlignment;
  /** Lit outline + table fill, for a shell whose inner control is open. */
  active: boolean;
  /** See {@link DataTableCellTriggerProps.outline}. */
  outline: boolean;
  className?: string;
  children?: React.ReactNode;
}>;

/**
 * The cell outline without a press target of its own — the frame an editor
 * wears while it is open, in place of the trigger it replaced, so entering
 * and leaving edit mode moves no glyph. Internal: a read-only cell composes
 * bare `DataTableCellContent` and wears no outline at all.
 *
 * The three appearance props are required rather than defaulted: the one call
 * site below states all three, so a default here would be a second answer
 * nothing ever asks for.
 */
function DataTableCellOutline({
  align,
  active,
  outline,
  className,
  children,
}: DataTableCellOutlineProps) {
  return (
    <Box
      {...CELL_SHELL_PROPS}
      className={cn(cellOutlineClassName(align, active, outline), className)}
    >
      {children}
    </Box>
  );
}

type DataTableCellTriggerProps = Omit<
  BoxProps<'button'>,
  'as' | 'align' | 'children'
> &
  Omittable<{
    align?: DataTableCellAlignment;
    disabled?: boolean;
    active?: boolean;
    /** When false, no ring, fill, or border renders in any state — for an
     *  editor whose activation lives elsewhere (an action-launched rename),
     *  where the caret alone marks editing. The border is dropped, not merely
     *  transparent: the rename's rest twin is a borderless cell, so a reserved
     *  1px here would shift the glyphs on entry. */
    outline?: boolean;
    /** The content carries press targets of its own — an avatar stack's
     *  trailing add slot, a chip with a press. A button nests no button, so
     *  the cell renders as a `role="button"` div instead: a click anywhere in
     *  the cell that no nested control claimed fires `onClick` (a press inside
     *  the picker a nested control opened does not — it is portaled, outside
     *  the cell's DOM), Enter/Space act on the focused cell, and the row's own
     *  click yields to it the way it yields to every div-press cell. */
    nestedControls?: boolean;
    /** Optional so the trigger can be a Base UI `render` element — the primitive
     *  supplies children at render time (same idiom as vendored `render={<Button/>}`). */
    children?: React.ReactNode;
  }>;

function DataTableCellTrigger({
  align = 'start',
  disabled,
  active = false,
  outline = true,
  nestedControls = false,
  className,
  children,
  onClick,
  onKeyDown,
  ...props
}: DataTableCellTriggerProps) {
  const shell = {
    ...CELL_SHELL_PROPS,
    className: cn(cellOutlineClassName(align, active, outline), className),
  };

  if (!nestedControls) {
    return (
      <Box<'button'>
        {...shell}
        as="button"
        disabled={disabled}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // No `as`: the Box keeps the button-shaped props it is handed (a label,
  // data attributes, handlers, the ref a Base UI `render` injects) and
  // renders Box's default element, a div — the one tag that may hold the
  // nested control. Nothing here reads a button-only member off the event.
  return (
    <Box<'button'>
      {...shell}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      data-row-click-stop=""
      cursor={disabled ? undefined : 'pointer'}
      onClick={(e) => {
        if (disabled || !isOwnPress(e.currentTarget, e.target)) return;
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        // Only the cell's own focus activates it: a key pressed on a nested
        // control is that control's.
        if (disabled || e.target !== e.currentTarget) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.currentTarget.click();
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export type DataTableEditableTextCellProps = Omittable<{
  value: string;
  onCommit: (nextValue: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  align?: DataTableCellAlignment;
  textBrightness?: DataTableTextBrightness;
  commitOnBlur?: boolean;
  'aria-label'?: string;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** When false, the cell draws no hover ring, active ring, or fill in any
   *  state — for an action-launched rename inside a cell whose row already
   *  carries the chrome; the caret alone marks editing. */
  outline?: boolean;
  /** For a cell whose resting display is bolder than body text (a name
   *  column's medium label): the editor and the rest state both wear it, so
   *  entering and leaving edit mode moves no glyph. */
  weight?: VariantProps<typeof dataTableEditableTextCellVariants>['weight'];
  /** A node seated ahead of the value inside the cell's own inset, in BOTH
   *  states — a glyph that leads the resting cell keeps its place while the
   *  value is edited instead of vanishing on entry. The resting cell is one
   *  button, so a leading node with a press of its own belongs only to a
   *  host that controls `editing` and renders its own rest twin (an
   *  action-launched rename). */
  leading?: React.ReactNode;
}>;

export function DataTableEditableTextCell({
  value,
  onCommit,
  placeholder,
  emptyLabel = '—',
  disabled,
  align = 'start',
  textBrightness = 'high',
  commitOnBlur = true,
  'aria-label': ariaLabel,
  editing,
  onEditingChange,
  outline = true,
  weight,
  leading,
}: DataTableEditableTextCellProps) {
  const density = useDataTableDensity();
  const densityConfig = DENSITY_CONFIG[density];
  const resolvedAriaLabel = ariaLabel ?? 'Edit cell value';
  const [uncontrolledEditing, setUncontrolledEditing] = React.useState(false);
  const isEditingControlled = editing !== undefined;
  const isEditing = isEditingControlled ? editing : uncontrolledEditing;
  const [draft, setDraft] = React.useState(value);
  const didSubmitRef = React.useRef(false);
  // Controlled editing is launched from OUTSIDE the cell — a row menu's
  // Rename — so its first blur can be the closing menu's own focus traffic.
  const reclaimFromMenuSteal = useMenuFocusStealGuard(
    isEditingControlled && isEditing,
  );

  const setEditing = React.useCallback(
    (nextEditing: boolean) => {
      if (!isEditingControlled) {
        setUncontrolledEditing(nextEditing);
      }
      onEditingChange?.(nextEditing);
    },
    [isEditingControlled, onEditingChange]
  );

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  const startEditing = React.useCallback(() => {
    // Backstop: a disabled cell renders a disabled trigger, so no press can
    // arrive here — only state drift between the prop and the DOM could.
    if (disabled) return;
    didSubmitRef.current = false;
    setDraft(value);
    setEditing(true);
  }, [disabled, value, setEditing]);

  const finishEditing = React.useCallback(
    (nextValue: string) => {
      if (disabled) return;
      setEditing(false);
      if (nextValue !== value) {
        onCommit(nextValue);
      }
    },
    [disabled, onCommit, setEditing, value]
  );

  const cancelEditing = React.useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [setEditing, value]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        cancelEditing();
      }
      if (event.key === 'Enter') {
        didSubmitRef.current = true;
        finishEditing(draft);
      }
    },
    [cancelEditing, finishEditing, draft]
  );

  // The editor sits inside the SAME shell + content padding the rest state
  // wears (trigger border → cell padding → text), with the themed active
  // ring saying "editing" — so entering and leaving edit mode moves no
  // glyph: same x-inset, same vertical centering, same size and weight.
  //
  // `gap-2` between the leading node and the value is the hover-actions
  // cell's own content gap, so a rename launched from that cell lands its
  // caret where the resting label sat.
  if (isEditing) {
    return (
      <DataTableCellOutline active align={align} outline={outline}>
        <DataTableCellContent align={align} className={cn(leading && 'gap-2')}>
          {leading}
          {/* `bare`: the cell owns ALL the metrics — the primitive's shell
              keeps an inner inset that px-0 cannot reach, which pushed the
              editing glyphs off the resting label's x. */}
          <Input
            bare
            value={draft}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          if (reclaimFromMenuSteal(event)) return;
          if (commitOnBlur && !didSubmitRef.current) {
            finishEditing(draft);
          } else {
            setEditing(false);
          }
        }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus
            className={cn(
              'h-full w-full min-w-0 bg-transparent',
              dataTableEditableTextCellVariants({ align, weight }),
              densityConfig.textSize
            )}
            aria-label={resolvedAriaLabel}
          />
        </DataTableCellContent>
      </DataTableCellOutline>
    );
  }

  const displayValue = value?.length ? value : emptyLabel;
  const variant = value?.length
    ? textBrightness === 'subtle'
      ? 'muted'
      : undefined
    : 'muted';

  return (
    <DataTableCellTrigger
      align={align}
      disabled={disabled}
      outline={outline}
      onClick={startEditing}
      aria-label={resolvedAriaLabel}
    >
      <DataTableCellContent align={align} className={cn(leading && 'gap-2')}>
        {leading}
        {/* size={null} suppresses Text's default responsive size classes so the
            density text-size class applies without a sm: residue. */}
        <Text
          size={null}
          variant={variant}
          className={cn(
            dataTableEditableTextCellVariants({ align, weight }),
            densityConfig.textSize
          )}
        >
          {displayValue}
        </Text>
      </DataTableCellContent>
    </DataTableCellTrigger>
  );
}

export type DataTablePopoverCellProps = Omittable<{
  children: React.ReactNode;
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
  align?: DataTableCellAlignment;
  disabled?: boolean;
  /** The trigger's accessible name, for a value with no text of its own (an
   *  empty cell's glyph, which a screen reader cannot read). */
  'aria-label'?: string;
  /**
   * Fires on every open transition — the hook for draft-on-close commits.
   * There is deliberately no `open` prop: the press gesture belongs to Base UI
   * (standalone) or to the shared overlay host (inside a table), so open state
   * has exactly one owner and a caller cannot drive it out of sync.
   */
  onOpenChange?: (open: boolean) => void;
  contentProps?: Omit<React.ComponentProps<typeof PopoverPopup>, 'children'>;
  /**
   * Present as a bottom sheet on a phone (`Popover responsive`). Default TRUE:
   * a cell's popup is a picker list whose search takes the keyboard, and a
   * popup hung off a cell in a sideways-scrolling grid has nowhere to go when
   * the keyboard rises. Pass false for a value editor that must stay anchored
   * to its cell. Tablets and desktops keep the popover either way.
   */
  responsive?: boolean;
  /** The phone sheet's caption: the column's name ("Assignees"), so a sheet
   *  of names says what it is. The popover shows nothing. */
  sheetTitle?: React.ReactNode;
}>;

/**
 * The press-to-popover wiring shared by every anchored popup a table cell can
 * open — the whole-cell {@link DataTablePopoverCell} and the in-cell
 * {@link DataTablePopoverTrigger}.
 *
 * Inside a DataTable this mounts NO overlay root of its own: the trigger is a
 * plain element and the table's shared popover host receives an anchor plus a
 * content thunk. That is what keeps a table of these cheap — see
 * `data-table-overlay.tsx` for why a root per cell is the thing to avoid.
 * Outside a DataTable there is no host (`hosted` is false), so the caller owns
 * a Popover root and drives it from `open`/`onOpenChange`.
 */
function useCellPopover({
  content,
  contentProps,
  onOpenChange,
  responsive = true,
  sheetTitle,
}: Pick<
  DataTablePopoverCellProps,
  'content' | 'contentProps' | 'onOpenChange' | 'responsive' | 'sheetTitle'
>) {
  const overlay = useDataTableOverlay();
  const cellId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  // Hosted, the open cell is whichever one the table's single popover is
  // anchored to; standalone, Base UI owns the popup and this only mirrors it
  // for the trigger's `active` styling.
  const [standaloneOpen, setStandaloneOpen] = React.useState(false);
  const open = overlay !== null ? overlay.activePopoverId === cellId : standaloneOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setStandaloneOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  const close = React.useCallback(() => handleOpenChange(false), [handleOpenChange]);

  // Normalize to a thunk so the shared host never builds content until it
  // opens — a static `content` node is constructed for every cell on every
  // render, closed popup or not.
  const contentRef = React.useRef(content);
  contentRef.current = content;
  const resolveContent = React.useCallback(
    (hostClose: () => void) => {
      const current = contentRef.current;
      return typeof current === 'function' ? current(hostClose) : current;
    },
    []
  );

  // Base UI dismisses a popup on POINTERDOWN, and this trigger sits outside
  // the popup (the host anchors manually, so there is no Popover.Trigger part
  // to register it). A press on the open cell therefore dismisses it before
  // the click arrives, and the click would reopen what the press just closed.
  // Decide the toggle on pointerdown — the same thing Popover.Trigger does —
  // and swallow the click that gesture produces. Keyboard activation emits a
  // click with no preceding pointerdown, so it still routes through onClick.
  const swallowClickRef = React.useRef(false);

  const handlePointerDown = React.useCallback(() => {
    if (!overlay || overlay.activePopoverId !== cellId) return;
    overlay.closeCellPopover();
    swallowClickRef.current = true;
  }, [overlay, cellId]);

  const handleClick = React.useCallback(() => {
    if (swallowClickRef.current) {
      swallowClickRef.current = false;
      return;
    }
    const anchor = triggerRef.current;
    // Backstop: this handler is only mounted on the hosted (overlay) render
    // path, and a click cannot land before the trigger ref attaches — only a
    // host swap mid-gesture could leave either side empty.
    if (!overlay || !anchor) return;
    overlay.openCellPopover({
      id: cellId,
      anchor,
      content: resolveContent,
      contentProps,
      onOpenChange: handleOpenChange,
      responsive,
      sheetTitle,
    });
  }, [overlay, cellId, resolveContent, contentProps, handleOpenChange, responsive, sheetTitle]);

  return {
    hosted: overlay !== null,
    open,
    responsive,
    sheetTitle,
    triggerRef,
    handlePointerDown,
    handleClick,
    handleOpenChange,
    close,
    resolveContent,
  };
}

/**
 * A cell whose press opens a popover — the whole cell is the trigger and wears
 * the editable-cell ring. See {@link useCellPopover} for the host wiring.
 */
export function DataTablePopoverCell({
  children,
  content,
  align = 'start',
  disabled,
  onOpenChange,
  contentProps,
  responsive,
  sheetTitle,
  'aria-label': ariaLabel,
}: DataTablePopoverCellProps) {
  const popover = useCellPopover({ content, contentProps, onOpenChange, responsive, sheetTitle });

  if (popover.hosted) {
    return (
      <DataTableCellTrigger
        ref={popover.triggerRef}
        align={align}
        aria-label={ariaLabel}
        disabled={disabled}
        active={popover.open}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        onPointerDown={popover.handlePointerDown}
        onClick={popover.handleClick}
      >
        <DataTableCellContent align={align}>{children}</DataTableCellContent>
      </DataTableCellTrigger>
    );
  }

  const resolvedContent = popover.open ? popover.resolveContent(popover.close) : null;

  return (
    // Controlled, not a mirror: content receives `close` (the hosted path's
    // hostClose), and an uncontrolled root would ignore it — the popup would
    // stand open and empty after a closeOnAction row action.
    <Popover
      open={popover.open}
      onOpenChange={popover.handleOpenChange}
      responsive={popover.responsive}
    >
      <PopoverTrigger
        render={
          <DataTableCellTrigger
            align={align}
            aria-label={ariaLabel}
            disabled={disabled}
            active={popover.open}
          />
        }
      >
        <DataTableCellContent align={align}>{children}</DataTableCellContent>
      </PopoverTrigger>
      <PopoverPopup align="start" sheetTitle={sheetTitle} {...contentProps}>
        {resolvedContent}
      </PopoverPopup>
    </Popover>
  );
}

// `content` is also a (never used) HTML attribute on the button props — omit
// it so the popup thunk's type is not intersected with `string`.
export type DataTablePopoverTriggerProps = Omit<
  useRender.ComponentProps<'button'>,
  'render' | 'children' | 'content'
> &
  Pick<
    DataTablePopoverCellProps,
    'content' | 'contentProps' | 'onOpenChange' | 'disabled' | 'responsive' | 'sheetTitle'
  > &
  Omittable<{
    /**
     * The press target — a Button, or a Tooltip trigger around one. An element,
     * not a render callback: the open state travels as `data-popup-open` and
     * `data-pressed` (what a Base UI `Popover.Trigger` stamps), so the leaf
     * styles itself the same way on both mount paths.
     */
    render: React.ReactElement<Record<string, unknown>>;
  }>;

/**
 * A press target INSIDE a cell that opens a popover anchored to itself — a
 * glyph that leads a name, a value's inline action — for a cell whose own press
 * does something else (opens the row's details) or that carries several
 * popups. The popup positions against this element, not the cell.
 *
 * Owns its press: the click stops at this element, so a hover-action cell's
 * open-details click does not also fire, and `data-row-click-stop` yields the
 * row's own click the same way. Inside a table the popup rides the shared
 * overlay host (one root per table, see `useCellPopover`); standalone it
 * owns a Popover root.
 */
export function DataTablePopoverTrigger({
  render,
  content,
  contentProps,
  onOpenChange,
  disabled,
  responsive,
  sheetTitle,
  onClick,
  ...props
}: DataTablePopoverTriggerProps) {
  const popover = useCellPopover({ content, contentProps, onOpenChange, responsive, sheetTitle });

  const stopClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick?.(event);
  };

  const hosted = useRender({
    render,
    ref: popover.triggerRef,
    // The data attributes travel as render state: `open` stamps what a Base
    // UI Popover.Trigger stamps, so the leaf styles itself identically on
    // both mount paths; `claimsRowClick` is the row-click yield.
    state: { open: popover.open, claimsRowClick: true },
    stateAttributesMapping: {
      open: (open) => (open ? { 'data-popup-open': '', 'data-pressed': '' } : null),
      claimsRowClick: () => ({ 'data-row-click-stop': '' }),
    },
    props: mergeProps<'button'>(
      {
        'aria-haspopup': 'dialog',
        'aria-expanded': popover.open,
        disabled,
        onPointerDown: popover.handlePointerDown,
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          stopClick(event);
          popover.handleClick();
        },
      },
      props
    ),
    // Standalone renders the Popover root below; the hosted element is the
    // only thing this hook produces there, so it is never mounted twice.
    enabled: popover.hosted,
  });

  if (popover.hosted) return hosted;

  const resolvedContent = popover.open ? popover.resolveContent(popover.close) : null;

  return (
    <Popover
      open={popover.open}
      onOpenChange={popover.handleOpenChange}
      responsive={popover.responsive}
    >
      <PopoverTrigger
        render={render}
        disabled={disabled}
        data-row-click-stop=""
        onClick={stopClick}
        {...props}
      />
      <PopoverPopup align="start" sheetTitle={sheetTitle} {...contentProps}>
        {resolvedContent}
      </PopoverPopup>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// DataTableEntitySelectorCell — multi-select with draft-on-close commit
// ---------------------------------------------------------------------------

export type DataTableEntitySelectorCellProps = Omittable<{
  /** EntitySelector configuration (pools, search placeholder, etc.) */
  config: EntitySelectorConfig;
  /** Currently committed IDs */
  value: string[];
  /** Called on popover close if selections changed */
  onCommit: (ids: string[]) => void;
  /** Mirrors the picker popover's open state — for trigger content that must
   *  react to it (e.g. suppressing a hover preview while the picker is up). */
  onOpenChange?: (open: boolean) => void;
  align?: DataTableCellAlignment;
  disabled?: boolean;
  contentProps?: Omit<React.ComponentProps<typeof PopoverPopup>, 'children'>;
  /** Heading of the popup's selected section (defaults to "Selected") — pass
   *  the entity noun so the popup reads like the shared pickers ("Teams" over
   *  an "All Teams" pool). */
  selectedLabel?: string;
  /** Per-row hover action for the selected section and every pool — the
   *  shared pickers' badge grammar. The button's label/tooltip come from each
   *  pool's `itemBadge`; a `closeOnAction` badge closes this cell's popover
   *  (committing the draft) before the action fires. */
  onItemAction?: (entity: EntityItemConfig) => void;
  /** See {@link DataTablePopoverCellProps}: the name of a trigger whose
   *  content has no text (an empty cell's glyph). */
  'aria-label'?: string;
  /** See {@link DataTablePopoverCellProps}: the phone sheet's caption. */
  sheetTitle?: React.ReactNode;
  /** Trigger display content */
  children: React.ReactNode;
}>;

export function DataTableEntitySelectorCell({
  config,
  value,
  onCommit,
  onOpenChange,
  align,
  disabled,
  contentProps,
  selectedLabel,
  onItemAction,
  sheetTitle,
  'aria-label': ariaLabel,
  children,
}: DataTableEntitySelectorCellProps) {
  const draftRef = React.useRef<string[]>(value);
  const [draftIds, setDraftIds] = React.useState<string[]>(value);

  // Build a lookup map from all pools
  const entityById = React.useMemo(() => {
    const map = new Map<string, { item: EntityItemConfig; poolId: string }>();
    for (const pool of config.pools) {
      for (const item of pool.items) {
        map.set(item.id, { item, poolId: pool.id });
      }
    }
    return map;
  }, [config.pools]);

  const selections = React.useMemo<EntitySelection[]>(
    () =>
      draftIds
        .map((id) => {
          const entry = entityById.get(id);
          if (!entry) return null;
          return { id, poolId: entry.poolId, entity: entry.item };
        })
        .filter(Boolean) as EntitySelection[],
    [draftIds, entityById]
  );

  const handleValueChange = React.useCallback((next: EntitySelection[]) => {
    const nextIds = next.map((s) => s.id);
    draftRef.current = nextIds;
    setDraftIds(nextIds);
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      onOpenChange?.(nextOpen);
      if (nextOpen) {
        // Reset draft to committed value on open
        draftRef.current = value;
        setDraftIds(value);
        return;
      }
      // Commit if changed
      const prev = new Set(value);
      const next = new Set(draftRef.current);
      if (prev.size !== next.size || [...prev].some((id) => !next.has(id))) {
        onCommit(draftRef.current);
      }
    },
    [value, onCommit, onOpenChange]
  );

  return (
    <DataTablePopoverCell
      align={align}
      aria-label={ariaLabel}
      disabled={disabled}
      sheetTitle={sheetTitle}
      onOpenChange={handleOpenChange}
      // The popup carries no padding of its own: EntitySelectorContent owns
      // the standard picker dressing (the fixed 280px rung, the 400px height
      // cap, the 6px search-well inset), so this popover is the same object
      // as every other selector popover.
      contentProps={{
        padding: 'none',
        ...contentProps,
      }}
      content={(close) => (
        // Inline variant: the host popover owns open state, so the selector's
        // open is pinned true and its close intents (a closeOnAction badge, a
        // single-select pick) route to the host's close — which runs the
        // commit-on-close above.
        <EntitySelector
          variant="inline"
          config={config}
          value={selections}
          onValueChange={handleValueChange}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) close();
          }}
        >
          <EntitySelectorContent>
            <EntitySelectorInput />
            {/* The sectioned selected list, not the chips strip: mounting it
              * removes chosen entities from the pools below, so a selection
              * is stated ONCE — as an untickable-into-duplicate row up top —
              * never as a chip AND a ticked row (the shared teams/assignee
              * picker grammar). */}
            <EntitySelectorSelected
              label={selectedLabel ?? "Selected"}
              hideWhenEmpty
              hideBadge
              hideSelectionHighlight
              onItemAction={onItemAction}
            />
            {config.pools.map((pool) => (
              <EntitySelectorPool
                key={pool.id}
                value={pool.id}
                hideSelectionHighlight
                onItemAction={onItemAction}
              />
            ))}
          </EntitySelectorContent>
        </EntitySelector>
      )}
    >
      {children}
    </DataTablePopoverCell>
  );
}

export { DataTableCellTrigger };
export type { DataTableCellTriggerProps };
